import { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ShoppingBag, Calendar, Edit2, Trash2, Download, CheckSquare, X, Plus, Printer, AlertCircle, Search, Loader2, Truck, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import OrderFilters from "./orders/OrderFilters";
import InvoicedControls from "./orders/InvoicedControls";
import XeroConnectionChip from "./orders/XeroConnectionChip";
import AdminPageHeader from "@/components/AdminPageHeader";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

const statusConfig = {
  pending: { label: "Pending", color: "bg-yellow-100 text-yellow-800 border-yellow-200" },
  approved: { label: "Approved", color: "bg-blue-100 text-blue-800 border-blue-200" },
  dispatched: { label: "Dispatched", color: "bg-green-100 text-green-800 border-green-200" },
  invoiced: { label: "Invoicing", color: "bg-purple-100 text-purple-800 border-purple-200" },
  canceled: { label: "Canceled", color: "bg-red-100 text-red-800 border-red-200" },
  packed: { label: "Packed", color: "bg-blue-100 text-blue-800 border-blue-200" },
  confirmed: { label: "Confirmed", color: "bg-blue-100 text-blue-800 border-blue-200" },
  shipped: { label: "Shipped", color: "bg-green-100 text-green-800 border-green-200" },
};

export default function OrdersTab({ ordersData, updateOrderStatus, groupBy, onGroupByChange, user, users, products, fetchAllData, initialStatusFilter, onFilterApplied }) {
  // Granular permission gates. ONLY `admin` is a super-user that bypasses
  // permission checks; `manager` is the internal "Staff" role and MUST be
  // gated by the explicit `user.permissions` array (otherwise the tab is
  // de-facto view-only-or-edit-all and the granular checkboxes are useless).
  // The Sidebar already gates the whole tab on `orders.view`, so by the time
  // we render here the user has at minimum read access. Each of the booleans
  // below adds capability on top of that.
  const isSuperAdmin = user?.role === "admin";
  const userPerms = useMemo(
    () => new Set(Array.isArray(user?.permissions) ? user.permissions : []),
    [user],
  );
  const canEditOrder = isSuperAdmin || userPerms.has("orders.edit");
  const canCancelOrder = isSuperAdmin || userPerms.has("orders.cancel");
  const canExport = isSuperAdmin || userPerms.has("orders.export");
  // Used to decide whether ANY interactive UI (checkboxes, bulk bar, group
  // "Select All" button) is worth showing. Pure view-only users get none.
  const canSelect = canEditOrder || canCancelOrder || canExport;
  
  const [statusFilter, setStatusFilter] = useState(initialStatusFilter || "pending");
  const [searchQuery, setSearchQuery] = useState("");
  const [editingOrder, setEditingOrder] = useState(null);
  const [editedItems, setEditedItems] = useState([]);
  const [editedDeliveryDate, setEditedDeliveryDate] = useState("");
  // Bulk status change — two-step confirmation flow (avoids accidental clicks)
  const [pendingBulkChange, setPendingBulkChange] = useState(null); // { newStatus, label }
  // When reverting approved → pending and one of the selected orders has
  // already-delivered bags, the backend returns 409 asking for a second
  // confirmation. We surface a small AlertDialog for it.
  const [forceRevertContext, setForceRevertContext] = useState(null); // { deliveredCount, orderCount }
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [editedAdjustments, setEditedAdjustments] = useState([]);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, order: null });
  
  // Create order for hotel states
  // (One customer user may own multiple hotels with distinct catalogues, so
  // selection is by hotel — not by user — to load the correct per-hotel
  // products & pricing.)
  const [showCreateForCustomerDialog, setShowCreateForCustomerDialog] = useState(false);
  const [allHotels, setAllHotels] = useState([]);
  const [hotelsLoading, setHotelsLoading] = useState(false);
  const [selectedHotel, setSelectedHotel] = useState(null);
  const [customerProducts, setCustomerProducts] = useState([]); // Products for selected hotel
  const [createOrderStep, setCreateOrderStep] = useState(1); // 1: select hotel, 2: create order
  const [customerSearch, setCustomerSearch] = useState("");
  const [newOrderItems, setNewOrderItems] = useState([{ product_id: "", quantity: 1 }]);
  const [newOrderAdjustments, setNewOrderAdjustments] = useState([]); // Adjustments for new order
  const [newOrderDate, setNewOrderDate] = useState("");
  const [noDeliveryDates, setNoDeliveryDates] = useState([]);
  const [creatingOrder, setCreatingOrder] = useState(false);
  const [cancelDialog, setCancelDialog] = useState({ open: false, order: null });
  
  // CSV export for Xero states
  const [exportingToXero, setExportingToXero] = useState(false);
  
  // Receipt printing states
  const [generatingReceipts, setGeneratingReceipts] = useState(false);
  
  // Clear selected orders + apply incoming initial filter when statusFilter
  // changes. Wrapped in a setter so we don't have to maintain a `useEffect`
  // that calls `setState` (which the linter flags as a side-effect anti-
  // pattern).
  const handleStatusFilterChange = (nextFilter) => {
    setStatusFilter(nextFilter);
    setSelectedOrders([]);
  };
  
  // Apply initial status filter when navigating from dashboard.
  // Uses the "derived state from prop" pattern (React docs) so we avoid
  // a setState-in-effect anti-pattern: when the incoming initialStatusFilter
  // changes, we update internal state during render via a comparison ref.
  const [lastSeenInitialFilter, setLastSeenInitialFilter] = useState(initialStatusFilter);
  if (initialStatusFilter !== lastSeenInitialFilter) {
    setLastSeenInitialFilter(initialStatusFilter);
    if (initialStatusFilter && initialStatusFilter !== statusFilter) {
      setStatusFilter(initialStatusFilter);
    }
    if (initialStatusFilter && onFilterApplied) {
      // Defer to a microtask so the parent's setState doesn't run during
      // this child's render phase.
      Promise.resolve().then(onFilterApplied);
    }
  }
  
  // Get all orders without filtering
  const allOrders = Object.values(ordersData.groups || {}).flat();
  
  // Load products for adding to orders
  useState(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${API}/products`, {
          headers: getAuthHeader(),
        });
        setAllProducts(response.data);
      } catch (error) {
        console.error("Failed to load products");
      }
    };
    fetchProducts();
  }, []);
  
  // Within the Invoicing view, a second-level filter splits by billing state.
  // "pending" bucket = invoiced (awaiting) + failed (retry). "collected" =
  // successfully paid. Failed orders live under "pending" per operator's
  // ask — the retry button is there and the row stays visible until
  // resolved.
  const [invoicingSubTab, setInvoicingSubTab] = useState("pending"); // "pending" | "collected"
  // order_id -> { amount_paid, fully_paid } straight from Xero (bulk query)
  const [xeroPaidMap, setXeroPaidMap] = useState({});

  useEffect(() => {
    if (statusFilter !== "invoiced") return;
    let alive = true;
    axios.get(`${API}/billing/xero-paid-summary`, { headers: { ...getAuthHeader(), "x-no-cache": "1" } })
      .then((r) => { if (alive) setXeroPaidMap(r.data?.items || {}); })
      .catch(() => {});
    return () => { alive = false; };
  }, [statusFilter, allOrders.length]);

  const filteredOrders = allOrders.filter(o => {
    // Searching spans ALL statuses (ignores the active tab) so an order is
    // always findable by number or hotel name.
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      return (
        (o.order_number || "").toLowerCase().includes(q) ||
        (o.hotel_name || "").toLowerCase().includes(q)
      );
    }
    if (statusFilter === "pending") return o.status === "pending";
    if (statusFilter === "approved") return o.status === "approved";
    if (statusFilter === "dispatched") return o.status === "dispatched";
    if (statusFilter === "invoiced") {
      if (o.status !== "invoiced") return false;
      if (invoicingSubTab === "collected") return o.billing_status === "collected";
      // pending bucket: everything that's not yet collected — invoiced,
      // failed, void, or the legacy null state for orders still awaiting
      // their first Stripe invoice.
      return o.billing_status !== "collected";
    }
    if (statusFilter === "canceled") return o.status === "canceled";
    return true;
  });

  // Invoicing view: newest first (delivery_date DESC, then created_at DESC).
  // Other views keep the backend's earliest-first order for operational flow.
  if (statusFilter === "invoiced") {
    filteredOrders.sort((a, b) => {
      const d = (b.delivery_date || "").localeCompare(a.delivery_date || "");
      if (d !== 0) return d;
      return (b.created_at || "").localeCompare(a.created_at || "");
    });
  }

  const regroupOrders = () => {
    if (groupBy === "date") {
      const grouped = {};
      filteredOrders.forEach(order => {
        const date = order.delivery_date || "No date";
        if (!grouped[date]) grouped[date] = [];
        grouped[date].push(order);
      });
      return grouped;
    } else if (groupBy === "hotel") {
      const grouped = {};
      filteredOrders.forEach(order => {
        const hotel = order.hotel_name || "Unknown";
        if (!grouped[hotel]) grouped[hotel] = [];
        grouped[hotel].push(order);
      });
      return grouped;
    } else if (groupBy === "route") {
      const grouped = {};
      filteredOrders.forEach(order => {
        const route = order.route_name || "No Route";
        if (!grouped[route]) grouped[route] = [];
        grouped[route].push(order);
      });
      return grouped;
    } else {
      return { "All": filteredOrders };
    }
  };

  const groupedOrders = regroupOrders();
  
  // Handle receipt printing
  const handlePrintReceipts = async () => {
    if (selectedOrders.length === 0) {
      toast.error("Please select at least one order to print receipts");
      return;
    }
    
    setGeneratingReceipts(true);
    
    try {
      const response = await axios.post(
        `${API}/receipts/generate-daily`,
        selectedOrders,
        { 
          headers: getAuthHeader(),
          responseType: 'blob'
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `receipts_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Receipts generated for ${selectedOrders.length} orders`);
      setSelectedOrders([]);
    } catch (error) {
      console.error("Receipt generation error:", error);
      toast.error(error.response?.data?.detail || "Failed to generate receipts");
    } finally {
      setGeneratingReceipts(false);
    }
  };
  
  // Handle CSV export for Xero - directly export selected orders
  const handleXeroExport = async () => {
    // Filter selected orders to only dispatched ones
    const orderIds = selectedOrders.filter(id => {
      const order = allOrders.find(o => o.id === id);
      return order && order.status === "dispatched";
    });
    
    if (orderIds.length === 0) {
      toast.error("Please select at least one dispatched order to export");
      return;
    }
    
    setExportingToXero(true);
    
    try {
      // Call API to get CSV file
      const response = await axios.post(
        `${API}/xero/export-csv`,
        orderIds,
        { 
          headers: getAuthHeader(),
          responseType: 'blob'
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `xero_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`CSV downloaded: ${orderIds.length} orders exported`);
      setSelectedOrders([]);
    } catch (error) {
      console.error("CSV export error:", error);
      toast.error(error.response?.data?.detail || "Failed to export CSV");
    } finally {
      setExportingToXero(false);
    }
  };

  // Handle export delivery addresses
  const handleExportAddresses = async () => {
    if (selectedOrders.length === 0) {
      toast.error("Please select at least one order");
      return;
    }

    try {
      const response = await axios.post(
        `${API}/orders/export-delivery-addresses`,
        selectedOrders,
        { headers: getAuthHeader() }
      );

      const hotels = response.data.hotels;
      
      if (hotels.length === 0) {
        toast.error("No hotel addresses found for selected orders");
        return;
      }

      // Create CSV content
      const headers = ["Hotel Name", "Hotel Code", "Address", "Delivery Date", "Order Count"];
      const rows = hotels.map(hotel => [
        hotel.hotel_name,
        hotel.hotel_code || "",
        hotel.hotel_address || "",
        hotel.delivery_date || "",
        hotel.order_count
      ]);

      const csvContent = [
        headers.join(","),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(","))
      ].join("\n");

      // Download CSV
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `delivery_addresses_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success(`Exported ${hotels.length} hotel addresses`);
    } catch (error) {
      console.error("Export addresses error:", error);
      toast.error(error.response?.data?.detail || "Failed to export addresses");
    }
  };


  const toggleOrderSelection = (orderId) => {
    setSelectedOrders(prev => 
      prev.includes(orderId) 
        ? prev.filter(id => id !== orderId)
        : [...prev, orderId]
    );
  };

  const requestBulkStatusChange = (newStatus, label) => {
    if (selectedOrders.length === 0) {
      toast.error("Please select orders first");
      return;
    }
    setPendingBulkChange({ newStatus, label });
  };

  const handleBulkStatusUpdate = async (forceClear = false) => {
    if (!pendingBulkChange || selectedOrders.length === 0) return;
    const newStatus = pendingBulkChange.newStatus;
    setBulkProcessing(true);
    try {
      const results = await Promise.allSettled(
        selectedOrders.map((orderId) =>
          axios.put(
            `${API}/orders/${orderId}/status`,
            { status: newStatus, force_clear: forceClear || undefined },
            { headers: getAuthHeader() }
          )
        )
      );
      const ok = results.filter((r) => r.status === "fulfilled").length;
      const failed = results.length - ok;
      const revertedRfids = results
        .filter((r) => r.status === "fulfilled")
        .reduce((sum, r) => sum + ((r.value?.data?.revert?.rfid_count) || 0), 0);
      const recallRfids = results
        .filter((r) => r.status === "fulfilled")
        .reduce((sum, r) => sum + ((r.value?.data?.recall?.rfid_count) || 0), 0);
      const packedRecalled = results
        .filter((r) => r.status === "fulfilled")
        .reduce((sum, r) => sum + ((r.value?.data?.recall?.recalled_bags_kept) || 0), 0);
      const bagsDropped = results
        .filter((r) => r.status === "fulfilled")
        .reduce((sum, r) => sum + ((r.value?.data?.recall?.in_warehouse_bags_dropped) || 0), 0);
      const runsDetached = results
        .filter((r) => r.status === "fulfilled")
        .reduce((sum, r) => sum + ((r.value?.data?.recall?.runs_detached) || 0), 0);

      // Detect the "has delivered bags — need force_clear" 409 signal.
      // If any (and only those) failures came back with that code and the
      // caller didn't already force, prompt for a second confirmation.
      const needsForce = !forceClear && results
        .filter((r) => r.status === "rejected")
        .filter((r) => r.reason?.response?.status === 409 &&
                       r.reason?.response?.data?.detail?.code === "has_delivered_bags");
      if (needsForce.length > 0 && ok + (needsForce.length) === results.length) {
        const totalDelivered = needsForce.reduce(
          (s, r) => s + (r.reason.response.data.detail.delivered_count || 0), 0
        );
        setForceRevertContext({ deliveredCount: totalDelivered, orderCount: needsForce.length });
        setBulkProcessing(false);
        return; // Wait for explicit confirmation modal.
      }

      if (ok > 0) {
        const parts = [];
        if (revertedRfids > 0) parts.push(`${revertedRfids} RFID(s) returned`);
        if (recallRfids > 0) parts.push(`${recallRfids} RFID(s) returned (recall)`);
        if (bagsDropped > 0) parts.push(`${bagsDropped} bag(s) deleted`);
        if (packedRecalled > 0) parts.push(`${packedRecalled} in-transit bag(s) marked recalled`);
        if (runsDetached > 0) parts.push(`${runsDetached} run(s) detached`);
        const desc = parts.length ? parts.join(" · ") : undefined;
        toast.success(`${ok} order(s) updated to ${newStatus}`, desc ? { description: desc } : undefined);
      }
      if (failed > 0) {
        const firstErr =
          results.find((r) => r.status === "rejected")?.reason?.response?.data?.detail ||
          "Some orders could not be updated";
        const msg = typeof firstErr === "string" ? firstErr : (firstErr?.message || JSON.stringify(firstErr));
        toast.error(`${failed} update(s) failed: ${msg}`);
      }
      setSelectedOrders([]);
      setPendingBulkChange(null);
      setForceRevertContext(null);
      if (fetchAllData) fetchAllData();
    } catch (error) {
      toast.error("Failed to update orders");
    } finally {
      setBulkProcessing(false);
    }
  };

  // Create order for hotel handlers
  const handleCreateForCustomer = async () => {
    setShowCreateForCustomerDialog(true);
    setCreateOrderStep(1);
    setSelectedHotel(null);
    setCustomerProducts([]);
    setNewOrderItems([{ product_id: "", quantity: 1 }]);
    setNewOrderAdjustments([]);
    setNewOrderDate("");
    setCustomerSearch("");
    axios.get(`${API}/no-delivery-dates`, { headers: getAuthHeader() })
      .then((r) => setNoDeliveryDates(r.data?.dates || []))
      .catch(() => {});
    // Refresh the hotels list every time the dialog opens so newly-added
    // hotels show up without a page reload.
    setHotelsLoading(true);
    try {
      const res = await axios.get(`${API}/hotels`, { headers: getAuthHeader() });
      setAllHotels(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error("Failed to load hotels", e);
      toast.error("Failed to load hotels");
      setAllHotels([]);
    } finally {
      setHotelsLoading(false);
    }
  };

  const handleHotelSelect = async (hotel) => {
    setSelectedHotel(hotel);

    // Load THIS HOTEL's product catalogue (with hotel-specific pricing).
    // Backend falls back to the user's default catalogue if no per-hotel
    // override exists, so this works for single- and multi-hotel users alike.
    try {
      const response = await axios.get(
        `${API}/users/${hotel.owner_user_id}/products?hotel_id=${encodeURIComponent(hotel.id)}`,
        { headers: getAuthHeader() },
      );
      const customerProductData = response.data.products || [];

      // Enrich with full product details from the global products list.
      // Drop any rows whose product no longer exists in the catalog so
      // "Unknown Product" entries don't pollute the dropdown.
      const enrichedProducts = customerProductData
        .map((cp) => {
          const fullProduct = products?.find((p) => p.id === cp.product_id);
          if (!fullProduct) return null;
          return {
            product_id: cp.product_id,
            product_name: fullProduct.name,
            price: cp.price,
            minimum_quantity: fullProduct.minimum_quantity || 1,
          };
        })
        .filter(Boolean);

      setCustomerProducts(enrichedProducts);
      setCreateOrderStep(2);
    } catch (error) {
      console.error("Failed to load hotel products:", error);
      toast.error("Failed to load products for this hotel");
      setCustomerProducts([]);
    }
  };

  const addOrderItem = () => {
    setNewOrderItems([...newOrderItems, { product_id: "", quantity: 1 }]);
  };

  const updateOrderItem = (index, field, value) => {
    const updated = [...newOrderItems];
    updated[index][field] = value;
    setNewOrderItems(updated);
  };

  const removeOrderItem = (index) => {
    if (newOrderItems.length > 1) {
      setNewOrderItems(newOrderItems.filter((_, i) => i !== index));
    }
  };

  // Adjustment management for new orders — qty × unit price, amount computed
  const addNewOrderAdjustment = () => {
    setNewOrderAdjustments([...newOrderAdjustments, { type: "discount", quantity: 1, unit_amount: 0, reason: "" }]);
  };

  const updateNewOrderAdjustment = (index, field, value) => {
    const updated = [...newOrderAdjustments];
    updated[index][field] = value;
    setNewOrderAdjustments(updated);
  };

  const removeNewOrderAdjustment = (index) => {
    setNewOrderAdjustments(newOrderAdjustments.filter((_, i) => i !== index));
  };

  // Create-for-customer override warnings (admin can proceed anyway)
  const newOrderDateWarnings = (() => {
    if (!newOrderDate) return [];
    const w = [];
    if (noDeliveryDates.includes(newOrderDate)) w.push("This is a no-delivery day (public holiday) in system settings");
    const deadline = new Date(`${newOrderDate}T00:00:00`);
    deadline.setDate(deadline.getDate() - 1);
    deadline.setHours(11, 0, 0, 0);
    if (new Date() >= deadline) w.push("Past the normal cutoff (11:00 AM the day before delivery)");
    return w;
  })();

  const itemQtyWarning = (item) => {
    const p = customerProducts.find((cp) => cp.product_id === item.product_id);
    const min = p?.minimum_quantity || 1;
    const qty = parseInt(item.quantity) || 0;
    if (min <= 1 || qty <= 0) return null;
    if (qty < min) return `Below minimum of ${min}`;
    if (qty % min !== 0) return `Not a multiple of ${min} (e.g. ${min}, ${min * 2}, ${min * 3}…)`;
    return null;
  };

  const handleSubmitOrderForCustomer = async () => {
    const filledItems = newOrderItems.filter(item => item.product_id);
    const validAdjustments = newOrderAdjustments.filter(
      adj => (parseInt(adj.quantity) || 0) > 0 && (parseFloat(adj.unit_amount) || 0) > 0
    );
    if (!selectedHotel || !newOrderDate) {
      toast.error("Please fill all required fields");
      return;
    }
    if (filledItems.length === 0 && validAdjustments.length === 0) {
      toast.error("Add at least one product or one adjustment");
      return;
    }

    setCreatingOrder(true);
    try {
      // Build items with full product info
      const items = filledItems.map(item => {
        const product = customerProducts.find(p => p.product_id === item.product_id);
        if (!product) {
          throw new Error(`Product not found: ${item.product_id}`);
        }
        return {
          product_id: item.product_id,
          product_name: product.product_name,
          quantity: parseInt(item.quantity),
          price_per_unit: product.price
        };
      });

      const orderData = {
        user_id: selectedHotel.owner_user_id,
        hotel_id: selectedHotel.id,
        delivery_date: newOrderDate,
        items: items,
        adjustments: validAdjustments.map(adj => ({
          type: adj.type,
          quantity: parseInt(adj.quantity) || 1,
          unit_amount: parseFloat(adj.unit_amount),
          amount: (parseInt(adj.quantity) || 1) * parseFloat(adj.unit_amount),
          reason: adj.reason || ""
        }))
      };

      await axios.post(`${API}/orders`, orderData, { headers: getAuthHeader() });
      toast.success(`Order created for ${selectedHotel.hotel_name}`);
      setShowCreateForCustomerDialog(false);
      if (fetchAllData) fetchAllData();
    } catch (error) {
      console.error("Create order error:", error);
      toast.error(error.response?.data?.detail || error.message || "Failed to create order");
    } finally {
      setCreatingOrder(false);
    }
  };

  // Admin — or any Staff (role=manager) with the `orders.create_for_customer`
  // perm — can create orders for a hotel.
  const canCreateForCustomer = isSuperAdmin || userPerms.has("orders.create_for_customer");

  const handleEditOrder = async (order) => {
    setEditingOrder(order);
    setEditedItems([...order.items]);
    setEditedDeliveryDate((order.delivery_date || "").slice(0, 10));
    
    // Initialize adjustments with type + qty/unit derived from stored fields
    const adjustmentsWithType = order.adjustments ? order.adjustments.map(adj => {
      const qty = parseInt(adj.quantity) || 1;
      const unit = adj.unit_amount != null ? Math.abs(adj.unit_amount) : Math.abs(adj.amount || 0) / qty;
      return { ...adj, quantity: qty, unit_amount: unit, type: adj.amount < 0 ? "deduct" : "add" };
    }) : [];
    setEditedAdjustments(adjustmentsWithType);
    
    // Load all products + the destination hotel's assigned product catalog
    // in parallel. Each hotel under one customer user can have its own
    // catalogue & pricing — `?hotel_id=<order.hotel_id>` returns the
    // hotel-specific catalog (falling back to the user-default if no
    // per-hotel override exists, which keeps single-hotel customers
    // working). The Add-Products dropdown filters by `customerProducts`
    // so operators only see products that exist in *this hotel's* price
    // list and don't get the confusing "not assigned to this customer"
    // toast after picking.
    try {
      const userProductsUrl = order.hotel_id
        ? `${API}/users/${order.user_id}/products?hotel_id=${encodeURIComponent(order.hotel_id)}`
        : `${API}/users/${order.user_id}/products`;
      const [productsRes, userProductsRes] = await Promise.all([
        allProducts.length === 0
          ? axios.get(`${API}/products`, { headers: getAuthHeader() })
          : Promise.resolve({ data: allProducts }),
        axios.get(userProductsUrl, { headers: getAuthHeader() }),
      ]);
      if (allProducts.length === 0) setAllProducts(productsRes.data);
      setCustomerProducts(userProductsRes.data?.products || []);
    } catch (error) {
      toast.error("Failed to load product catalog for this hotel");
    }
    
    setShowEditDialog(true);
  };

  const updateItemQuantity = (index, newQuantity) => {
    const qty = parseInt(newQuantity) || 0;
    const newItems = [...editedItems];
    newItems[index].quantity = qty;
    setEditedItems(newItems);
  };

  const removeItem = (index) => {
    const newItems = editedItems.filter((_, i) => i !== index);
    setEditedItems(newItems);
  };

  const addProductToOrder = (productId) => {
    const product = allProducts.find(p => p.id === productId);
    if (!product) return;
    // customerProducts is loaded in handleEditOrder; the dropdown already
    // filters by this list, so we don't expect a miss here — but stay defensive.
    const userProduct = customerProducts.find(p => p.product_id === productId);
    if (!userProduct) {
      toast.error("This product is not assigned to this customer");
      return;
    }
    const newItem = {
      product_id: productId,
      product_name: product.name,
      quantity: product.minimum_quantity,
      price_per_unit: userProduct.price,
    };
    setEditedItems([...editedItems, newItem]);
  };

  const addAdjustment = () => {
    setEditedAdjustments([...editedAdjustments, { description: "", quantity: 1, unit_amount: 0, amount: 0, type: "deduct" }]);
  };

  const updateAdjustment = (index, field, value) => {
    const newAdjustments = [...editedAdjustments];
    const adj = newAdjustments[index];
    if (field === "quantity") {
      adj.quantity = Math.max(1, parseInt(value) || 1);
    } else if (field === "unit_amount") {
      adj.unit_amount = Math.abs(parseFloat(value) || 0);
    } else if (field === "type") {
      adj.type = value;
    } else {
      adj[field] = value;
      setEditedAdjustments(newAdjustments);
      return;
    }
    // Recompute signed total = qty × unit, sign from type.
    const total = (adj.quantity || 1) * (adj.unit_amount || 0);
    adj.amount = (adj.type || "deduct") === "deduct" ? -total : total;
    setEditedAdjustments(newAdjustments);
  };

  const removeAdjustment = (index) => {
    const newAdjustments = editedAdjustments.filter((_, i) => i !== index);
    setEditedAdjustments(newAdjustments);
  };

  const saveEditedOrder = async () => {
    if (editedItems.length === 0 && editedAdjustments.length === 0) {
      toast.error("Order must have at least one item or one adjustment");
      return;
    }

    // Validate adjustments
    for (let adj of editedAdjustments) {
      if (!adj.description.trim()) {
        toast.error("All adjustments must have a description");
        return;
      }
    }

    try {
      await axios.put(
        `${API}/orders/${editingOrder.id}/admin-edit`,
        { 
          items: editedItems,
          adjustments: editedAdjustments,
          notes: editingOrder.notes,
          delivery_date: editedDeliveryDate || undefined,
          is_pickup: !!editingOrder.is_pickup,
        },
        { headers: getAuthHeader() }
      );
      toast.success("Order updated successfully");
      setShowEditDialog(false);
      if (fetchAllData) fetchAllData();
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      const errorMessage = typeof errorDetail === 'string' 
        ? errorDetail 
        : errorDetail?.msg || "Failed to update order";
      toast.error(errorMessage);
    }
  };

  const handleExport = async () => {
    if (selectedOrders.length === 0) {
      toast.error("Please select at least one order to export");
      return;
    }

    try {
      const selectedOrderData = allOrders.filter(o => selectedOrders.includes(o.id));

      // Create detailed CSV content
      let csvContent = "Date,Weekday,Hotel Name,Order Number,Product,Quantity,Price Per Unit,Total,Status,Notes\n";

      selectedOrderData
        .slice()
        .sort((a, b) => (a.delivery_date || "").localeCompare(b.delivery_date || ""))
        .forEach(order => {
          const dateRaw = order.delivery_date;
          const date = dateRaw ? new Date(dateRaw).toLocaleDateString("en-US") : "";
          const weekday = dateRaw ? new Date(dateRaw).toLocaleDateString("en-US", { weekday: "long" }) : "";

          order.items.forEach(item => {
            const hotelName = `"${order.hotel_name}"`;
            const orderNum = order.order_number;
            const productName = `"${item.product_name}"`;
            const qty = item.quantity;
            const pricePerUnit = item.price_per_unit.toFixed(2);
            const itemTotal = (item.quantity * item.price_per_unit).toFixed(2);
            const status = order.status;
            const notes = order.notes ? `"${order.notes.replace(/"/g, '""')}"` : "";

            csvContent += `${date},${weekday},${hotelName},${orderNum},${productName},${qty},${pricePerUnit},${itemTotal},${status},${notes}\n`;
          });
        });

      // Download CSV
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `orders_detailed_${statusFilter}_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();

      toast.success(`Exported ${selectedOrders.length} selected order(s)`);
    } catch (error) {
      toast.error("Failed to export orders");
    }
  };

  const handleCancelOrder = async () => {
    if (!cancelDialog.order) return;

    try {
      await axios.put(
        `${API}/orders/${cancelDialog.order.id}/status`,
        { status: "canceled", notes: "Canceled by admin" },
        { headers: getAuthHeader() }
      );
      toast.success("Order canceled successfully");
      setCancelDialog({ open: false, order: null });
      if (fetchAllData) fetchAllData();
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      const errorMessage = typeof errorDetail === 'string' 
        ? errorDetail 
        : errorDetail?.msg || "Failed to cancel order";
      toast.error(errorMessage);
    }
  };

  const handleDeleteOrder = async () => {
    if (!deleteDialog.order) return;

    try {
      await axios.delete(`${API}/orders/${deleteDialog.order.id}/permanent-delete`, {
        headers: getAuthHeader()
      });
      toast.success("Order permanently deleted");
      setDeleteDialog({ open: false, order: null });
      if (fetchAllData) fetchAllData();
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      const errorMessage = typeof errorDetail === 'string' 
        ? errorDetail 
        : errorDetail?.msg || "Failed to delete order";
      toast.error(errorMessage);
    }
  };

  const handleExportApproved = async () => {
    try {
      // Get selected orders that are approved
      const selectedApprovedOrders = filteredOrders.filter(o => 
        selectedOrders.includes(o.id) && o.status === "approved"
      );
      
      if (selectedApprovedOrders.length === 0) {
        toast.error("Please select approved orders to export");
        return;
      }

      // Create CSV content
      let csvContent = "Hotel,Order Number,Delivery Date,Product,Quantity,Price per Unit,Total Price\n";
      
      // Product summary for totals
      const productTotals = {};
      
      // Add each order
      selectedApprovedOrders.forEach(order => {
        order.items.forEach(item => {
          const row = [
            `"${order.hotel_name}"`,
            order.order_number,
            order.delivery_date,
            `"${item.product_name}"`,
            item.quantity,
            `$${item.price_per_unit.toFixed(2)}`,
            `$${(item.quantity * item.price_per_unit).toFixed(2)}`
          ].join(",");
          csvContent += row + "\n";
          
          // Accumulate product totals
          if (!productTotals[item.product_name]) {
            productTotals[item.product_name] = 0;
          }
          productTotals[item.product_name] += item.quantity;
        });
      });
      
      // Add summary section
      csvContent += "\n\nPRODUCT SUMMARY - TOTAL QUANTITIES\n";
      csvContent += "Product,Total Quantity\n";
      Object.entries(productTotals)
        .sort(([a], [b]) => a.localeCompare(b))
        .forEach(([product, total]) => {
          csvContent += `"${product}",${total}\n`;
        });
      
      // Create and download file
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `approved_orders_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success("Approved orders exported successfully");
    } catch (error) {
      toast.error("Failed to export approved orders");
    }
  };

  return (
    <>
    <Card className="shadow-lg border-0">
      <CardHeader>
        <AdminPageHeader
          title="Orders Management"
          description="Manage and track all customer orders"
          className="mb-4"
          actions={
            <>
            {canCreateForCustomer && statusFilter === "pending" && (
              <Button 
                onClick={handleCreateForCustomer}
                className="bg-brand-navy hover:bg-brand-navy-hover text-white rounded-sm"
                data-testid="create-for-customer-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create for Customer
              </Button>
            )}
            {statusFilter === "approved" && selectedOrders.length > 0 && canExport && (
              <>
                <Button
                  onClick={handleExportApproved}
                  variant="outline"
                  className="bg-green-50 hover:bg-green-100 border-green-300 rounded-sm"
                  data-testid="export-approved-button"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export Approved ({selectedOrders.length})
                </Button>
                <Button
                  onClick={handlePrintReceipts}
                  variant="outline"
                  disabled={generatingReceipts}
                  className="bg-purple-50 hover:bg-purple-100 border-purple-300 rounded-sm"
                  data-testid="print-receipts-button"
                >
                  <Printer className="w-4 h-4 mr-2" />
                  {generatingReceipts ? "Generating..." : `Print Receipts (${selectedOrders.length})`}
                </Button>
              </>
            )}
            {statusFilter !== "approved" && selectedOrders.length > 0 && canExport && (
              <Button onClick={handleExport} variant="outline" className="rounded-sm" data-testid="export-button">
                <Download className="w-4 h-4 mr-2" />
                Export ({selectedOrders.length})
              </Button>
            )}
            {statusFilter === "dispatched" && selectedOrders.length > 0 && canExport && (
              <>
                <Button 
                  onClick={handleXeroExport} 
                  variant="outline"
                  disabled={exportingToXero}
                  className="bg-blue-50 hover:bg-blue-100 border-blue-300 rounded-sm"
                  data-testid="xero-export-button"
                >
                  <Download className="w-4 h-4 mr-2" />
                  {exportingToXero ? "Exporting..." : `Export to Xero (${selectedOrders.filter(id => {
                    const order = allOrders.find(o => o.id === id);
                    return order && order.status === "dispatched";
                  }).length})`}
                </Button>
                <Button 
                  onClick={handleExportAddresses} 
                  variant="outline"
                  className="bg-orange-50 hover:bg-orange-100 border-orange-300 rounded-sm"
                  data-testid="export-addresses-button"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export Addresses ({selectedOrders.length})
                </Button>
              </>
            )}
            {canEditOrder && selectedOrders.length > 0 && statusFilter === "all" && (
              <div className="text-xs text-muted-foreground italic">
                Pick a status filter to bulk-update
              </div>
            )}
            </>
          }
        />
        
        <div className="flex flex-wrap gap-3 items-center">
          <OrderFilters
            statusFilter={statusFilter}
            onChange={handleStatusFilterChange}
            allOrders={allOrders}
          />

          <div className="relative w-full sm:w-auto">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 pointer-events-none" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search order # or hotel…"
              className="pl-8 pr-8 h-9 w-full sm:w-56"
              data-testid="orders-search-input"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-700"
                data-testid="orders-search-clear"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          {searchQuery.trim() && (
            <span className="text-xs text-muted-foreground" data-testid="orders-search-hint">
              Searching all statuses · {filteredOrders.length} found
            </span>
          )}

          {statusFilter === "invoiced" && <XeroConnectionChip />}

          {statusFilter === "invoiced" && (
            <div className="inline-flex rounded-md border bg-white overflow-hidden" data-testid="invoicing-subtabs">
              <button
                type="button"
                onClick={() => setInvoicingSubTab("pending")}
                data-testid="invoicing-subtab-pending"
                className={`px-3 py-1.5 text-xs font-semibold ${invoicingSubTab === "pending" ? "bg-amber-400 text-neutral-900" : "text-neutral-600 hover:bg-neutral-50"}`}
              >
                To collect ·{" "}
                {allOrders.filter(o => o.status === "invoiced" && o.billing_status !== "collected").length}
              </button>
              <button
                type="button"
                onClick={() => setInvoicingSubTab("collected")}
                data-testid="invoicing-subtab-collected"
                className={`px-3 py-1.5 text-xs font-semibold border-l ${invoicingSubTab === "collected" ? "bg-emerald-600 text-white" : "text-neutral-600 hover:bg-neutral-50"}`}
              >
                Collected ·{" "}
                {allOrders.filter(o => o.status === "invoiced" && o.billing_status === "collected").length}
              </button>
            </div>
          )}

          <Select value={groupBy} onValueChange={onGroupByChange}>
            <SelectTrigger data-testid="group-by-select" className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">All (No Grouping)</SelectItem>
              <SelectItem value="date">Group by Date</SelectItem>
              <SelectItem value="hotel">Group by Hotel</SelectItem>
              <SelectItem value="route">Group by Route</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {filteredOrders.length === 0 ? (
            <div className="text-center py-12">
              <ShoppingBag className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500 text-lg">No {statusFilter} orders</p>
            </div>
          ) : (
            Object.entries(groupedOrders).map(([groupKey, orders]) => (
              <div key={groupKey} className="space-y-3">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  {groupBy === "date" ? (
                    <>
                      <span className="bg-blue-100 text-blue-800 px-3 py-1.5 rounded-lg text-sm">
                        {new Date(groupKey).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        })}
                      </span>
                      <Badge className="bg-gray-300 text-gray-700 text-xs">
                        {new Date(groupKey).toLocaleDateString("en-US", { weekday: "long" })}
                      </Badge>
                    </>
                  ) : (
                    <span className="bg-indigo-100 text-indigo-800 px-3 py-1.5 rounded-lg text-sm">{groupKey}</span>
                  )}
                  <Badge className="bg-gray-200 text-gray-700 text-xs">{orders.length}</Badge>
                  {canSelect && (
                    <Button
                      onClick={() => {
                        const allGroupOrderIds = orders.map(o => o.id);
                        const allSelected = allGroupOrderIds.every(id => selectedOrders.includes(id));
                        if (allSelected) {
                          // Deselect all
                          setSelectedOrders(prev => prev.filter(id => !allGroupOrderIds.includes(id)));
                        } else {
                          // Select all
                          setSelectedOrders(prev => [...new Set([...prev, ...allGroupOrderIds])]);
                        }
                      }}
                      variant="outline"
                      size="sm"
                      className="ml-2"
                    >
                      <CheckSquare className="w-4 h-4 mr-1" />
                      {orders.every(o => selectedOrders.includes(o.id)) ? "Deselect All" : "Select All"}
                    </Button>
                  )}
                </h3>
                {orders.map((order) => (
                  <Card
                    key={order.id}
                    data-testid={`order-${order.id}`}
                    className={`border ${order.is_rewash ? "border-amber-300 bg-amber-50/40" : ""}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        {canSelect && (
                          <Checkbox
                            checked={selectedOrders.includes(order.id)}
                            onCheckedChange={() => toggleOrderSelection(order.id)}
                            data-testid={`select-order-${order.id}`}
                          />
                        )}
                        <div className="flex-1">
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <p className="font-semibold flex items-center gap-2 flex-wrap">
                                {order.hotel_name}
                                {order.is_pickup && (
                                  <Badge
                                    className="bg-orange-100 text-orange-900 border border-orange-300 text-[10px] uppercase tracking-[0.16em] font-bold px-2 py-0.5"
                                    data-testid={`pickup-badge-${order.id}`}
                                  >
                                    <Truck className="w-3 h-3 mr-1" />
                                    Pickup
                                  </Badge>
                                )}
                                {order.is_rewash && (
                                  <Badge
                                    className="bg-amber-100 text-amber-900 border border-amber-300 text-[10px] uppercase tracking-[0.16em] font-bold px-2 py-0.5"
                                    data-testid="rewash-badge"
                                  >
                                    ↺ Rewash · free
                                  </Badge>
                                )}
                              </p>
                              <p className="text-xs text-gray-500">#{order.order_number} • @{order.username}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              {canEditOrder && (
                                <Button
                                  onClick={() => handleEditOrder(order)}
                                  data-testid={`edit-order-${order.id}`}
                                  variant="outline"
                                  size="sm"
                                >
                                  <Edit2 className="w-3 h-3 mr-1" />
                                  Edit
                                </Button>
                              )}
                              {canCancelOrder && order.status === "pending" && (
                                <Button
                                  onClick={() => setCancelDialog({ open: true, order })}
                                  data-testid={`cancel-order-${order.id}`}
                                  variant="outline"
                                  size="sm"
                                  className="text-orange-600 border-orange-300 hover:bg-orange-50"
                                  title="Cancel this pending order"
                                >
                                  <X className="w-3 h-3 mr-1" />
                                  Cancel
                                </Button>
                              )}
                              {canCancelOrder && order.status === "canceled" && (
                                <Button
                                  onClick={() => setDeleteDialog({ open: true, order })}
                                  data-testid={`delete-order-${order.id}`}
                                  variant="destructive"
                                  size="sm"
                                  title="Permanently delete this canceled order"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              )}
                              {order.status === "invoiced" && (
                                <InvoicedControls
                                  order={order}
                                  xeroPaid={xeroPaidMap[order.id]}
                                  onChanged={fetchAllData}
                                />
                              )}
                              <Badge className={`${statusConfig[order.status]?.color || "bg-gray-100"} border text-xs`}>
                                {statusConfig[order.status]?.label || order.status}
                              </Badge>
                            </div>
                          </div>
                          <div className="text-xs text-gray-600 mb-2">
                            {order.items.map((item, idx) => (
                              <span key={idx}>
                                {item.product_name} ×{item.quantity}
                                {idx < order.items.length - 1 ? ", " : ""}
                              </span>
                            ))}
                          </div>
                          {order.status === "dispatched" && (() => {
                            const batches = order.pick_batches || [];
                            const dispatchedCount =
                              (order.picked_rfids || []).length ||
                              batches.reduce((s, b) => s + ((b.rfid_codes || []).length || 0), 0);
                            // No pick data at all → this order pre-dates the picking feature,
                            // don't render a misleading "0 / X" badge.
                            if (dispatchedCount === 0 && batches.length === 0) return null;
                            const orderedTotal = (order.items || []).reduce((s, it) => s + (it.quantity || 0), 0);
                            const extras = (order.pick_extras || []).reduce((s, e) => s + (e.count || 0), 0);
                            return (
                              <div
                                data-testid={`dispatched-count-${order.id}`}
                                className="flex items-center flex-wrap gap-2 text-xs mb-2 px-2 py-1.5 rounded border border-green-200 bg-green-50/70"
                              >
                                <span className="text-green-800 font-semibold">
                                  Dispatched {dispatchedCount} / {orderedTotal}
                                </span>
                                {dispatchedCount < orderedTotal && (
                                  <span className="text-amber-700">· short {orderedTotal - dispatchedCount}</span>
                                )}
                                {extras > 0 && (
                                  <span className="text-amber-700">· +{extras} extra</span>
                                )}
                                {batches.length > 0 && (
                                  <span className="text-muted-foreground">· {batches.length} batch{batches.length === 1 ? "" : "es"}</span>
                                )}
                                {order.picked_by_name && (
                                  <span className="text-muted-foreground">· by {order.picked_by_name}</span>
                                )}
                              </div>
                            );
                          })()}
                          <div className="space-y-2">
                            <div className="flex justify-between items-center text-xs">
                              <span className="text-gray-500">
                                <Calendar className="w-3 h-3 inline mr-1" />
                                {new Date(order.delivery_date).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                              </span>
                              <span className="font-bold text-blue-600">
                                ${order.total_price.toFixed(2)} <span className="text-gray-500 text-xs">(incl. GST)</span>
                              </span>
                            </div>
                            
                            {/* Price Breakdown */}
                            {(
                              <div className="bg-gray-50 rounded p-2 text-xs space-y-1 border">
                              {/* Calculate subtotal from items */}
                              {(() => {
                                const subtotal = order.items.reduce((sum, item) => sum + (item.price_per_unit * item.quantity), 0);
                                const adjustmentTotal = (order.adjustments || []).reduce((sum, adj) => sum + adj.amount, 0);
                                const totalBeforeGST = subtotal + adjustmentTotal;
                                const gst = totalBeforeGST * 0.10;
                                
                                return (
                                  <>
                                    <div className="flex justify-between text-gray-700">
                                      <span>Subtotal:</span>
                                      <span>${subtotal.toFixed(2)}</span>
                                    </div>
                                    
                                    {order.adjustments && order.adjustments.length > 0 && (
                                      <>
                                        {order.adjustments.map((adj, idx) => (
                                          <div key={idx} className={`flex justify-between ${adj.amount < 0 ? 'text-green-600' : 'text-orange-600'}`}>
                                            <span className="truncate max-w-[180px]">
                                              {adj.description}
                                              {(adj.quantity || 1) > 1 && ` ×${adj.quantity} @$${(adj.unit_amount != null ? adj.unit_amount : Math.abs(adj.amount) / (adj.quantity || 1)).toFixed(2)}`}
                                              :
                                            </span>
                                            <span>{adj.amount > 0 ? '+' : ''}${adj.amount.toFixed(2)}</span>
                                          </div>
                                        ))}
                                        <div className="flex justify-between text-gray-700 pt-1 border-t">
                                          <span>After Adjustments:</span>
                                          <span>${totalBeforeGST.toFixed(2)}</span>
                                        </div>
                                      </>
                                    )}
                                    
                                    <div className="flex justify-between text-gray-700">
                                      <span>GST (10%):</span>
                                      <span>${gst.toFixed(2)}</span>
                                    </div>
                                    
                                    <div className="flex justify-between font-bold text-blue-600 pt-1 border-t">
                                      <span>Total:</span>
                                      <span>${order.total_price.toFixed(2)}</span>
                                    </div>
                                  </>
                                );
                              })()}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ))
          )}
        </div>
      </CardContent>

      {/* Edit Order Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Edit Order #{editingOrder?.order_number}</DialogTitle>
            <DialogDescription>
              Modify items for {editingOrder?.hotel_name}
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-3 rounded-md border bg-neutral-50">
              <div>
                <Label className="text-[11px] uppercase tracking-[0.16em] font-semibold text-muted-foreground">
                  Delivery date
                </Label>
                <Input
                  type="date"
                  value={editedDeliveryDate}
                  onChange={(e) => setEditedDeliveryDate(e.target.value)}
                  className="mt-1"
                  data-testid="edit-delivery-date"
                  disabled={editingOrder && !["pending", "approved"].includes(editingOrder.status)}
                />
                {editingOrder && !["pending", "approved"].includes(editingOrder.status) && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Delivery date is locked once the order is {editingOrder.status}.
                  </p>
                )}
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-[0.16em] font-semibold text-muted-foreground">
                  Hotel
                </Label>
                <p className="text-sm font-semibold mt-2">{editingOrder?.hotel_name}</p>
              </div>
              <div className="md:col-span-2">
                <label className="flex items-start gap-3 rounded-md border border-neutral-200 bg-white px-3 py-2 cursor-pointer hover:bg-neutral-50 transition-colors">
                  <input
                    type="checkbox"
                    data-testid="edit-order-is-pickup"
                    checked={!!editingOrder?.is_pickup}
                    onChange={(e) =>
                      setEditingOrder((prev) => (prev ? { ...prev, is_pickup: e.target.checked } : prev))
                    }
                    className="mt-1 h-4 w-4"
                  />
                  <span className="flex-1">
                    <span className="block text-sm font-semibold">This is a pickup order</span>
                    <span className="block text-xs text-muted-foreground mt-0.5">
                      Adds a small <strong>Pickup</strong> tag wherever the order appears
                      (Orders list, Picking and Delivery views).
                    </span>
                  </span>
                </label>
              </div>
            </div>
            <div>
              <Label className="text-sm font-semibold mb-2 block">Current Items</Label>
              {editedItems.map((item, index) => (
                <div key={index} className="flex items-center gap-2 p-2 border rounded mb-2">
                  <div className="flex-1">
                    <p className="font-medium text-sm">{item.product_name}</p>
                    <p className="text-xs text-gray-500">${item.price_per_unit} / unit</p>
                  </div>
                  <Input
                    type="number"
                    min="0"
                    value={item.quantity}
                    onChange={(e) => updateItemQuantity(index, e.target.value)}
                    className="w-20 h-8 text-sm"
                    data-testid={`edit-quantity-${index}`}
                  />
                  <span className="w-20 text-right text-sm font-semibold">
                    ${(item.quantity * item.price_per_unit).toFixed(2)}
                  </span>
                  <Button
                    onClick={() => removeItem(index)}
                    variant="outline"
                    size="sm"
                    className="text-red-600 h-8 w-8 p-0"
                    data-testid={`remove-item-${index}`}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              ))}
            </div>
            
            <div>
              <Label className="text-sm font-semibold mb-2 block">Add Products</Label>
              <Select onValueChange={addProductToOrder}>
                <SelectTrigger data-testid="add-product-select">
                  <SelectValue placeholder="Select product to add" />
                </SelectTrigger>
                <SelectContent>
                  {(() => {
                    // Only show products assigned to this customer AND not
                    // already in the order. Sorted by the customer's own
                    // sort_order so the dropdown matches their catalog.
                    const alreadyAdded = new Set(editedItems.map(it => it.product_id));
                    const rows = [...customerProducts]
                      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
                      .map(cp => {
                        const prod = allProducts.find(p => p.id === cp.product_id);
                        if (!prod) return null;
                        if (alreadyAdded.has(prod.id)) return null;
                        return (
                          <SelectItem key={prod.id} value={prod.id}>
                            {prod.name}
                          </SelectItem>
                        );
                      })
                      .filter(Boolean);
                    if (rows.length === 0) {
                      return (
                        <div className="px-3 py-2 text-xs text-muted-foreground">
                          {customerProducts.length === 0
                            ? "No products are assigned to this hotel."
                            : "All registered products are already in the order."}
                        </div>
                      );
                    }
                    return rows;
                  })()}
                </SelectContent>
              </Select>
            </div>

            <div className="border-t pt-4">
              <div className="flex justify-between items-center mb-2">
                <Label className="text-sm font-semibold">Adjustments (Returns, Deductions)</Label>
                <Button onClick={addAdjustment} size="sm" variant="outline" className="text-green-600">
                  <Plus className="w-3 h-3 mr-1" />
                  Add Adjustment
                </Button>
              </div>
              <p className="text-xs text-gray-500 mb-3">Add price adjustments for returns, discounts, or extra charges.</p>
              {editedAdjustments.map((adj, index) => (
                <div key={index} className="flex items-center gap-2 p-2 border rounded mb-2 bg-yellow-50">
                  <Input
                    type="text"
                    placeholder="Description (e.g., 3 sheets returned)"
                    value={adj.description}
                    onChange={(e) => updateAdjustment(index, "description", e.target.value)}
                    className="flex-1 h-8 text-sm"
                  />
                  <Select 
                    value={adj.type || (adj.amount < 0 ? "deduct" : "add")} 
                    onValueChange={(value) => updateAdjustment(index, "type", value)}
                  >
                    <SelectTrigger className="w-28 h-8 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="deduct">Deduct (-)</SelectItem>
                      <SelectItem value="add">Add (+)</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="flex items-center gap-1">
                    <Input
                      type="number"
                      step="1"
                      min="1"
                      title="Quantity"
                      value={adj.quantity ?? 1}
                      onChange={(e) => updateAdjustment(index, "quantity", e.target.value)}
                      className="w-16 h-8 text-sm"
                      data-testid={`edit-adj-qty-${index}`}
                    />
                    <span className="text-xs text-gray-500">×</span>
                    <span className="text-sm text-gray-600">$</span>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      title="Unit price"
                      value={adj.unit_amount ?? 0}
                      onChange={(e) => updateAdjustment(index, "unit_amount", e.target.value)}
                      className="w-20 h-8 text-sm"
                      data-testid={`edit-adj-unit-${index}`}
                    />
                    <span className="text-xs font-semibold text-gray-700 whitespace-nowrap">
                      = ${Math.abs(adj.amount || 0).toFixed(2)}
                    </span>
                  </div>
                  <Button
                    onClick={() => removeAdjustment(index)}
                    variant="outline"
                    size="sm"
                    className="text-red-600 h-8 w-8 p-0"
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
          
          <div className="pt-4 border-t">
            <div className="space-y-2 mb-4">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Items Subtotal:</span>
                <span className="font-semibold">
                  ${editedItems.reduce((sum, item) => sum + item.quantity * item.price_per_unit, 0).toFixed(2)}
                </span>
              </div>
              {editedAdjustments.length > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Adjustments:</span>
                  <span className={`font-semibold ${editedAdjustments.reduce((sum, adj) => sum + adj.amount, 0) < 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {editedAdjustments.reduce((sum, adj) => sum + adj.amount, 0) >= 0 ? '+' : ''}
                    ${editedAdjustments.reduce((sum, adj) => sum + adj.amount, 0).toFixed(2)}
                  </span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Subtotal (before GST):</span>
                <span className="font-semibold">
                  ${(editedItems.reduce((sum, item) => sum + item.quantity * item.price_per_unit, 0) + editedAdjustments.reduce((sum, adj) => sum + adj.amount, 0)).toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">GST (10%):</span>
                <span className="font-semibold">
                  ${((editedItems.reduce((sum, item) => sum + item.quantity * item.price_per_unit, 0) + editedAdjustments.reduce((sum, adj) => sum + adj.amount, 0)) * 0.10).toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between items-center pt-2 border-t">
                <span className="font-semibold">Total (inc GST):</span>
                <span className="font-display text-3xl font-extrabold text-brand-navy">
                  ${((editedItems.reduce((sum, item) => sum + item.quantity * item.price_per_unit, 0) + editedAdjustments.reduce((sum, adj) => sum + adj.amount, 0)) * 1.10).toFixed(2)}
                </span>
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => setShowEditDialog(false)} variant="outline" className="flex-1">
                Cancel
              </Button>
              <Button onClick={saveEditedOrder} className="flex-1" data-testid="save-edited-order">
                Save Changes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cancel Order Confirmation */}
      <AlertDialog open={cancelDialog.open} onOpenChange={(open) => setCancelDialog({ open, order: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Order?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>Are you sure you want to cancel this pending order? The order status will be changed to &quot;Canceled&quot;.</p>
              <div className="bg-orange-50 p-3 rounded border border-orange-200">
                <p className="font-semibold text-sm">Order: #{cancelDialog.order?.order_number}</p>
                <p className="text-sm">Hotel: {cancelDialog.order?.hotel_name}</p>
                <p className="text-sm">Status: <span className="font-semibold uppercase text-yellow-600">{cancelDialog.order?.status}</span></p>
              </div>
              <p className="text-gray-600 text-sm">The order will still be visible in the system with &quot;Canceled&quot; status and can be permanently deleted later if needed.</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setCancelDialog({ open: false, order: null })}>
              Keep Order
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancelOrder}
              className="bg-orange-600 hover:bg-orange-700"
            >
              <X className="w-4 h-4 mr-2" />
              Cancel Order
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Order Confirmation */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, order: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Permanently Delete Canceled Order?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>Are you sure you want to <strong>permanently delete</strong> this canceled order? This will remove it from the system completely.</p>
              <div className="bg-red-50 p-3 rounded border border-red-200">
                <p className="font-semibold text-sm">Order: #{deleteDialog.order?.order_number}</p>
                <p className="text-sm">Hotel: {deleteDialog.order?.hotel_name}</p>
                <p className="text-sm">Status: <span className="font-semibold uppercase text-red-600">{deleteDialog.order?.status}</span></p>
              </div>
              <p className="text-red-600 font-semibold">⚠️ This action cannot be undone! The order will be permanently removed from the database.</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteDialog({ open: false, order: null })}>
              Keep Order
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteOrder}
              className="bg-red-600 hover:bg-red-700"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Permanently Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Create Order for Customer Dialog */}
      <Dialog open={showCreateForCustomerDialog} onOpenChange={setShowCreateForCustomerDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Order for Hotel</DialogTitle>
            <DialogDescription>
              {createOrderStep === 1
                ? "Select a destination hotel"
                : `Creating order for: ${selectedHotel?.hotel_name}`}
            </DialogDescription>
          </DialogHeader>

          {createOrderStep === 1 && (
            <div className="space-y-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                <Input
                  data-testid="create-order-hotel-search"
                  value={customerSearch}
                  onChange={(e) => setCustomerSearch(e.target.value)}
                  placeholder="Search hotel name, code, address, username or email…"
                  className="pl-9"
                  autoFocus
                />
              </div>
              {hotelsLoading ? (
                <p className="text-sm text-muted-foreground text-center py-6">Loading hotels…</p>
              ) : (() => {
                const q = customerSearch.trim().toLowerCase();
                const list = (allHotels || [])
                  .filter((h) => !h.is_archived)
                  .filter((h) => {
                    if (!q) return true;
                    const blob = `${h.hotel_name || ""} ${h.hotel_code || ""} ${h.hotel_address || ""} ${h.suburb || ""} ${h.owner_username || ""} ${h.owner_email || ""}`.toLowerCase();
                    return blob.includes(q);
                  })
                  .sort((a, b) => (a.hotel_name || "").localeCompare(b.hotel_name || ""));
                if (list.length === 0) {
                  return (
                    <p className="text-sm text-muted-foreground text-center py-6">
                      {q ? `No hotels match "${customerSearch}"` : "No hotels available."}
                    </p>
                  );
                }
                return (
                  <div className="grid grid-cols-1 gap-2 max-h-96 overflow-y-auto">
                    {list.map((hotel) => (
                      <Button
                        key={hotel.id}
                        variant="outline"
                        className="h-auto py-3 justify-start text-left hover:bg-blue-50"
                        onClick={() => handleHotelSelect(hotel)}
                        data-testid={`create-order-hotel-${hotel.id}`}
                      >
                        <div className="flex-1">
                          <p className="font-semibold flex items-center gap-2">
                            {hotel.hotel_name}
                            {hotel.hotel_code && (
                              <span className="text-xs font-normal text-gray-500">[{hotel.hotel_code}]</span>
                            )}
                            {hotel.is_primary && (
                              <span className="text-[10px] uppercase tracking-wide bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">primary</span>
                            )}
                          </p>
                          {hotel.hotel_address && (
                            <p className="text-xs text-gray-500 mt-0.5">{hotel.hotel_address}</p>
                          )}
                          <p className="text-xs text-gray-400 mt-1">
                            Owner: {hotel.owner_username || hotel.owner_email}
                          </p>
                        </div>
                      </Button>
                    ))}
                  </div>
                );
              })()}
            </div>
          )}

          {createOrderStep === 2 && (
            <div className="space-y-4">
              <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                <p className="font-semibold text-sm">
                  {selectedHotel?.hotel_name}
                  {selectedHotel?.hotel_code ? ` [${selectedHotel.hotel_code}]` : ""}
                </p>
                {selectedHotel?.hotel_address && (
                  <p className="text-xs text-gray-600">{selectedHotel.hotel_address}</p>
                )}
                <p className="text-xs text-gray-500 mt-0.5">
                  Owner: {selectedHotel?.owner_username || selectedHotel?.owner_email}
                </p>
              </div>

              <div className="space-y-2">
                <Label>Delivery Date *</Label>
                <Input
                  type="date"
                  value={newOrderDate}
                  onChange={(e) => setNewOrderDate(e.target.value)}
                  required
                  className={newOrderDateWarnings.length ? "border-red-400 focus-visible:ring-red-400" : ""}
                  data-testid="create-order-delivery-date"
                />
                {newOrderDateWarnings.map((w, i) => (
                  <p key={i} className="text-xs text-red-600 font-semibold" data-testid="create-order-date-warning">
                    ⚠ {w} — you can still create this order (admin override)
                  </p>
                ))}
              </div>

              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <Label>Order Items *</Label>
                  <Button size="sm" variant="outline" onClick={addOrderItem}>
                    <Plus className="w-3 h-3 mr-1" />
                    Add Item
                  </Button>
                </div>

                {customerProducts.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">
                    No products assigned to this customer. Please assign products first.
                  </p>
                ) : (
                  newOrderItems.map((item, index) => {
                    const selectedProduct = customerProducts.find(p => p.product_id === item.product_id);
                    return (
                      <div key={index} className="border rounded-lg p-3 space-y-2">
                        <div className="flex gap-2 items-start">
                          <div className="flex-1">
                            <Label className="text-xs">Product</Label>
                            <Select
                              value={item.product_id}
                              onValueChange={(value) => updateOrderItem(index, "product_id", value)}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select product" />
                              </SelectTrigger>
                              <SelectContent>
                                {customerProducts?.map(product => (
                                  <SelectItem key={product.product_id} value={product.product_id}>
                                    {product.product_name} - ${product.price}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          <div className="w-24">
                            <Label className="text-xs">Quantity</Label>
                            <Input
                              type="number"
                              min="1"
                              value={item.quantity}
                              onChange={(e) => updateOrderItem(index, "quantity", e.target.value)}
                              className={itemQtyWarning(item) ? "border-red-400 focus-visible:ring-red-400" : ""}
                              data-testid={`create-order-qty-${index}`}
                            />
                          </div>

                          {newOrderItems.length > 1 && (
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => removeOrderItem(index)}
                              className="mt-5"
                            >
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </Button>
                          )}
                        </div>
                        
                        {itemQtyWarning(item) && (
                          <p className="text-xs text-red-600 font-semibold" data-testid={`create-order-qty-warning-${index}`}>
                            ⚠ {itemQtyWarning(item)} — allowed as admin override
                          </p>
                        )}
                        {selectedProduct && (
                          <div className="text-xs text-gray-600 bg-gray-50 p-2 rounded">
                            Price: ${selectedProduct.price} × {item.quantity} = ${(selectedProduct.price * item.quantity).toFixed(2)}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>

              {/* Adjustments Section */}
              <div className="space-y-3 border-t pt-3">
                <div className="flex justify-between items-center">
                  <Label>Adjustments (Optional)</Label>
                  <Button size="sm" variant="outline" onClick={addNewOrderAdjustment}>
                    <Plus className="w-3 h-3 mr-1" />
                    Add Adjustment
                  </Button>
                </div>

                {newOrderAdjustments.map((adjustment, index) => (
                  <div key={index} className="border rounded-lg p-3 space-y-2 bg-amber-50">
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <Label className="text-xs">Type</Label>
                        <Select
                          value={adjustment.type}
                          onValueChange={(value) => updateNewOrderAdjustment(index, "type", value)}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="discount">Discount (-)</SelectItem>
                            <SelectItem value="fee">Extra Fee (+)</SelectItem>
                            <SelectItem value="refund">Refund (-)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="w-20">
                        <Label className="text-xs">Qty</Label>
                        <Input
                          type="number"
                          min="1"
                          step="1"
                          value={adjustment.quantity ?? 1}
                          onChange={(e) => updateNewOrderAdjustment(index, "quantity", e.target.value)}
                          data-testid={`new-adj-qty-${index}`}
                        />
                      </div>

                      <div className="w-28">
                        <Label className="text-xs">Unit ($)</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={adjustment.unit_amount ?? 0}
                          onChange={(e) => updateNewOrderAdjustment(index, "unit_amount", e.target.value)}
                          data-testid={`new-adj-unit-${index}`}
                        />
                      </div>

                      <div className="w-24 pt-5 text-sm font-semibold text-gray-700">
                        = ${(((parseInt(adjustment.quantity) || 1) * (parseFloat(adjustment.unit_amount) || 0))).toFixed(2)}
                      </div>

                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => removeNewOrderAdjustment(index)}
                        className="mt-5"
                      >
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </Button>
                    </div>

                    <div>
                      <Label className="text-xs">Reason</Label>
                      <Input
                        placeholder="e.g., Bulk discount, Delivery fee"
                        value={adjustment.reason}
                        onChange={(e) => updateNewOrderAdjustment(index, "reason", e.target.value)}
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex gap-2 justify-end pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => {
                    setCreateOrderStep(1);
                    setSelectedHotel(null);
                  }}
                >
                  Back
                </Button>
                <Button
                  onClick={handleSubmitOrderForCustomer}
                  disabled={creatingOrder}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {creatingOrder ? "Creating..." : "Create Order"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

    </Card>

    {/* ----- Floating bulk-action bar (fixed at viewport bottom) -----
        Rendered via a portal into <body> so it ESCAPES the ancestor
        `.fade-in` wrapper in AdminDashboard, which animates `transform`
        and thereby becomes the containing block for `position:fixed`
        descendants — trapping the bar inside the scrollable main area.
        Portaling lifts it back out so `fixed bottom-0` truly anchors
        to the viewport regardless of page scroll position. */}
    {canEditOrder && selectedOrders.length > 0 && createPortal(
      <div
        data-testid="bulk-action-bar"
        className="fixed bottom-0 left-0 right-0 z-50 pointer-events-none"
      >
        <div className="mx-auto max-w-3xl px-4 pb-4 pointer-events-auto">
          <div className="rounded-xl shadow-2xl border border-neutral-200 bg-white p-3 flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2 mr-auto">
              <Badge className="bg-brand-navy text-white border-0 font-mono text-sm px-2.5 py-1">
                {selectedOrders.length}
              </Badge>
              <span className="text-sm font-medium">
                order{selectedOrders.length === 1 ? "" : "s"} selected
              </span>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setSelectedOrders([])}>
              Clear
            </Button>
            {/* Status-specific transitions */}
            {statusFilter === "pending" && (
              <Button
                size="sm"
                className="bg-brand-navy hover:bg-brand-navy-hover"
                onClick={() => requestBulkStatusChange("approved", "Approved")}
                data-testid="bulk-action-approve"
              >
                → Approve
              </Button>
            )}
            {statusFilter === "approved" && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => requestBulkStatusChange("pending", "Pending")}
                data-testid="bulk-action-back-pending"
                title="Recalls any packed bags + returns RFIDs to warehouse. Delivered bags need a second confirmation."
              >
                ← Back to Pending
              </Button>
            )}
            {statusFilter === "dispatched" && (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => requestBulkStatusChange("approved", "Approved (revert)")}
                  data-testid="bulk-action-revert"
                  className="border-amber-300 text-amber-800 hover:bg-amber-50"
                >
                  ↩ Revert (returns RFIDs)
                </Button>
                <Button
                  size="sm"
                  className="bg-green-700 hover:bg-green-800 text-white"
                  onClick={() => requestBulkStatusChange("invoiced", "Invoicing")}
                  data-testid="bulk-action-invoice"
                >
                  → Invoiced
                </Button>
              </>
            )}
            {statusFilter === "invoiced" && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => requestBulkStatusChange("dispatched", "Dispatched")}
                data-testid="bulk-action-back-dispatched"
              >
                ← Back to Dispatched
              </Button>
            )}
          </div>
        </div>
      </div>,
      document.body
    )}

    {/* ----- Confirmation dialog for bulk status change ----- */}
    <Dialog
      open={!!pendingBulkChange}
      onOpenChange={(v) => !v && !bulkProcessing && setPendingBulkChange(null)}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-brand-navy">
            Confirm status change
          </DialogTitle>
          <DialogDescription>
            About to update{" "}
            <span className="font-mono font-bold">{selectedOrders.length}</span> order
            {selectedOrders.length === 1 ? "" : "s"} to{" "}
            <span className="font-semibold">{pendingBulkChange?.label}</span>.
          </DialogDescription>
        </DialogHeader>
        {pendingBulkChange?.newStatus === "approved" && statusFilter === "dispatched" && (
          <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-3 text-sm text-amber-900">
            <p className="font-semibold flex items-center gap-2">
              <AlertCircle className="h-4 w-4" /> RFID revert side effect
            </p>
            <p className="mt-1">
              All RFIDs scanned in these orders will be returned to the warehouse
              and the picking history will be cleared so they can be re-picked.
            </p>
          </div>
        )}
        {pendingBulkChange?.newStatus === "approved" && statusFilter === "pending" && (
          <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-3 text-sm text-blue-900">
            <p className="font-semibold flex items-center gap-2">
              <AlertCircle className="h-4 w-4" /> What happens next
            </p>
            <p className="mt-1">
              Orders will be queued for warehouse picking. Once bags are packed and RFIDs scanned, they can be dispatched.
            </p>
          </div>
        )}
        {pendingBulkChange?.newStatus === "invoiced" && (
          <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-3 text-sm text-emerald-900">
            <p className="font-semibold flex items-center gap-2">
              <AlertCircle className="h-4 w-4" /> What happens next
            </p>
            <p className="mt-1">
              An invoice will be created in <strong>Xero</strong> for each order (no Stripe invoice — Stripe is only used when you press Collect). Cards will <strong>not</strong> be charged automatically — collect payment from the Invoicing tab when you&apos;re ready, or record a manual payment (bank transfer / cash).
            </p>
          </div>
        )}
        {pendingBulkChange?.newStatus === "pending" && statusFilter === "approved" && (
          <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-3 text-sm text-amber-900">
            <p className="font-semibold flex items-center gap-2">
              <AlertCircle className="h-4 w-4" /> Recall side effects
            </p>
            <ul className="mt-1 list-disc pl-5 space-y-0.5">
              <li>Any packed bags are marked <strong>recalled</strong> and RFIDs returned to the warehouse.</li>
              <li>The order is removed from any planned delivery run.</li>
              <li>If a driver scans a recalled bag on the PDA, it flashes <strong>&ldquo;ORDER RECALLED — contact office&rdquo;</strong>.</li>
              <li>If any bag was already <strong>delivered</strong>, you&apos;ll be asked to confirm a second time.</li>
            </ul>
          </div>
        )}
        {pendingBulkChange?.newStatus === "dispatched" && statusFilter === "invoiced" && (
          <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-3 text-sm text-amber-900">
            <p className="font-semibold flex items-center gap-2">
              <AlertCircle className="h-4 w-4" /> Invoice void side effect
            </p>
            <p className="mt-1">
              The Stripe invoice, Xero invoice and any Xero Stripe-fee bills for these orders will be voided. Any recorded partial payments must be refunded manually first.
            </p>
          </div>
        )}
        <DialogFooter className="mt-2">
          <Button
            variant="outline"
            onClick={() => setPendingBulkChange(null)}
            disabled={bulkProcessing}
          >
            Cancel
          </Button>
          <Button
            onClick={() => handleBulkStatusUpdate(false)}
            disabled={bulkProcessing}
            className="bg-brand-navy hover:bg-brand-navy-hover"
            data-testid="bulk-confirm"
          >
            {bulkProcessing ? "Updating..." : "Confirm"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Second-tier confirm when one or more selected orders already had
        delivered bags — we require an explicit "yes I understand" from
        the operator before the backend un-delivers the RFIDs. */}
    <Dialog
      open={!!forceRevertContext}
      onOpenChange={(v) => !v && !bulkProcessing && setForceRevertContext(null)}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-red-700">
            Force revert — delivered bags will be un-delivered
          </DialogTitle>
          <DialogDescription>
            <span className="font-mono font-bold">{forceRevertContext?.orderCount || 0}</span>{" "}
            of the selected orders already had{" "}
            <span className="font-mono font-bold">{forceRevertContext?.deliveredCount || 0}</span>{" "}
            delivered bag(s). Reverting to Pending will:
          </DialogDescription>
        </DialogHeader>
        <ul className="mt-1 list-disc pl-6 text-sm text-red-800 space-y-1">
          <li><strong>Un-deliver</strong> the delivered bags — the RFIDs return to the warehouse.</li>
          <li>Mark those bags as <strong>recalled</strong>.</li>
          <li>The customer's ledger is not affected here; if invoices were already issued, void them manually first.</li>
        </ul>
        <DialogFooter className="mt-3">
          <Button variant="outline" onClick={() => setForceRevertContext(null)} disabled={bulkProcessing} data-testid="force-revert-cancel">
            Cancel
          </Button>
          <Button
            onClick={() => handleBulkStatusUpdate(true)}
            disabled={bulkProcessing}
            className="bg-red-700 hover:bg-red-800 text-white"
            data-testid="force-revert-confirm"
          >
            {bulkProcessing ? "Reverting..." : "Yes — force revert"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}
